import { lazy, Suspense } from 'react';

import { useAuth } from '@/auth/useAuth';
import { LoadingLayout } from '@/layouts/LoadingLayout';

import { RootRedirect } from './RootRedirect';

/**
 * The landing page is its own lazy chunk — an authenticated visitor is redirected away from `/` and
 * never downloads it, and an anonymous one pulls no persona code to read it (ADR-0004). Same
 * treatment the public job board and employer directory already get.
 */
const LandingPage = lazy(() =>
  import('@/features/public-home/pages/LandingPage').then((m) => ({ default: m.LandingPage })),
);

/**
 * What `/` means, decided in one place (ADR-0035).
 *
 * <p>The root address has two audiences and answers each differently:</p>
 * <ul>
 *   <li><b>Anonymous</b> — the public landing page. This is the address someone types, the address a
 *       link in an email resolves to, and the first address a crawler fetches; before ADR-0035 all
 *       three got a login form.</li>
 *   <li><b>Authenticated</b> — `RootRedirect`, <b>unchanged</b>. Its whole resolution order (pending
 *       invitation → platform tier → membership-derived workspace → remembered workspace → onboarding
 *       chooser) still applies to exactly the accounts it applied to before. `GuestRoute` still sends
 *       an already-signed-in visitor to `ROUTES.root`, and they still land where they always did.</li>
 * </ul>
 *
 * <p><b>Why the loading branch is not the landing page.</b> `useAuth` starts in `isLoading` while the
 * silent session restore runs, so rendering the landing page during it would show every returning
 * user a marketing flash before their dashboard. `LoadingLayout` is what `ProtectedRoute` and
 * `GuestRoute` already do with the same state, and it costs a crawler nothing — Googlebot resolves as
 * anonymous, which is the branch below.</p>
 *
 * <p>This component reads auth state; it does not implement any. No token handling, no session logic,
 * nothing about `/login` or `/register` beyond the landing page's links to them.</p>
 */
export function RootRoute() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) return <LoadingLayout />;
  if (isAuthenticated) return <RootRedirect />;

  return (
    <Suspense fallback={<LoadingLayout />}>
      <LandingPage />
    </Suspense>
  );
}
